import { createContext, type ReactNode, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { pushTokenService } from "@/api/push-token";
import { notificationService } from "@/lib/notifications";

interface NotificationsContextType {
  isSubscribed: boolean | null;
  subscribe: () => Promise<void>;
  unsubscribe: () => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const [isSubscribed, setIsSubscribed] = useState<boolean | null>(null);

  useEffect(() => {
    const checkSubscription = async () => {
      const subscription = await notificationService.getSubscription();
      setIsSubscribed(!!subscription);
    };

    notificationService.init().then(checkSubscription);
  }, []);

  const subscribe = async () => {
    try {
      const subscription = await notificationService.subscribeToPush();
      if (subscription) {
        await pushTokenService.create({
          provider: "web",
          token: subscription.endpoint,
          device: navigator.userAgent,
          options: subscription.toJSON(),
        });
        setIsSubscribed(true);
      } else {
        // Permission denied or the push subscription failed (see console).
        toast.error("Couldn't enable push notifications in this browser.");
      }
    } catch (error) {
      console.error("Error subscribing to push notifications:", error);
      toast.error("Couldn't enable push notifications in this browser.");
      setIsSubscribed(false);
    }
  };

  const unsubscribe = async () => {
    try {
      const result = await notificationService.unsubscribe();
      if (result) {
        setIsSubscribed(false);
      }
    } catch (error) {
      console.error("Error unsubscribing from push notifications:", error);
      setIsSubscribed(true);
    }
  };

  return (
    <NotificationsContext.Provider value={{ isSubscribed, subscribe, unsubscribe }}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationsContext);
  if (context === undefined) {
    throw new Error("useNotifications must be used within a NotificationsProvider");
  }
  return context;
}
